import { useState, useMemo } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '../home/components/Navbar';
import Footer from '../home/components/Footer';

const highlightMatch = (text: string, query: string) => {
  if (!query.trim()) return text;
  const words = query.trim().toLowerCase().split(/\s+/).filter(w => w.length > 1);
  if (words.length === 0) return text;
  const regex = new RegExp(`(${words.map(w => w.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')).join('|')})`, 'gi');
  const parts = text.split(regex);
  return parts.map((part, i) =>
    words.some(w => part.toLowerCase() === w)
      ? <mark key={i} className="bg-yellow-200/70 text-inherit rounded-sm px-0.5">{part}</mark>
      : part
  );
};

const fuzzyMatch = (text: string, queryWords: string[]) => {
  const lower = text.toLowerCase();
  return queryWords.some(word => lower.includes(word));
};

const Topics = () => {
  const [searchQuery, setSearchQuery] = useState('');
  const [activeCategory, setActiveCategory] = useState('All');

  const getRiskBadge = (score: number) => {
    if (score >= 8) return { label: 'High Risk', color: '#ef4444', bg: '#fef2f2', border: '#fecaca' };
    if (score >= 5) return { label: 'Moderate Risk', color: '#f59e0b', bg: '#fffbeb', border: '#fde68a' };
    return { label: 'Low Risk', color: '#10b981', bg: '#ecfdf5', border: '#a7f3d0' };
  };

  const getRiskBar = (score: number) => {
    const maxScore = 10;
    const percentage = (score / maxScore) * 100;
    let color = '#10b981';
    if (score >= 8) color = '#ef4444';
    else if (score >= 5) color = '#f59e0b';
    return { percentage, color };
  };

  const keyTopics = [
    {
      title: 'Understanding Genetic High Cholesterol',
      description: 'Learn how inherited gene mutations cause dangerously elevated cholesterol from birth.',
      icon: 'ri-dna-line',
      link: '/understanding-genetic-high-cholesterol',
      color: '#f04e58',
      category: 'Cholesterol',
      riskScore: 9
    },
    {
      title: 'Familial Heart Diseases',
      description: 'Explore the spectrum of heart conditions that run in families and how to manage them.',
      icon: 'ri-heart-pulse-line',
      link: '/familial-heart-diseases',
      color: '#0d9488',
      category: 'Heart Disease',
      riskScore: 8
    },
    {
      title: 'FH in South Asian Population',
      description: 'Understand the unique prevalence and risks of familial hypercholesterolemia in South Asians.',
      icon: 'ri-global-line',
      link: '/fh-south-asian',
      color: '#e67e22',
      category: 'Risk Factors',
      riskScore: 8
    },
    {
      title: 'How to Identify FH',
      description: 'Key signs, symptoms, and diagnostic criteria to recognize familial hypercholesterolemia early.',
      icon: 'ri-stethoscope-line',
      link: '/how-to-identify-fh',
      color: '#8b5cf6',
      category: 'Heart Disease',
      riskScore: 7
    },
    {
      title: 'What is HeFH',
      description: 'Heterozygous FH — the most common inherited cholesterol disorder affecting 1 in 250 people.',
      icon: 'ri-test-tube-line',
      link: '/what-is-hefh',
      color: '#3b82f6',
      category: 'Cholesterol',
      riskScore: 7
    },
    {
      title: 'What is HoFH',
      description: 'Homozygous FH — a rare, severe form requiring aggressive treatment from early childhood.',
      icon: 'ri-capsule-line',
      link: '/what-is-hofh',
      color: '#ec4899',
      category: 'Cholesterol',
      riskScore: 10
    },
    {
      title: 'How is FH Treated',
      description: 'From statins to PCSK9 inhibitors — modern treatment strategies for managing familial hypercholesterolemia.',
      icon: 'ri-medicine-bottle-line',
      link: '/how-is-fh-treated',
      color: '#14b8a6',
      category: 'Treatment',
      riskScore: 6
    },
    {
      title: 'Lipid & Genetic Testing',
      description: 'Advanced lipid panels and genetic tests that reveal your true cardiovascular risk profile.',
      icon: 'ri-microscope-line',
      link: '/lipid-genetic-testing',
      color: '#f59e0b',
      category: 'Cholesterol',
      riskScore: 5
    }
  ];

  const topics = [
    {
      title: 'Pregnancy + Familial Hypercholesterolemia (FH)',
      description: 'Understanding cardiovascular considerations, medication safety, and genetic transmission during pregnancy for women with FH...',
      category: 'Women\'s Health',
      link: '/pregnancy-fh',
      riskScore: 8
    },
    {
      title: 'Structural & Valvular Heart Disease + Familial Hypercholesterolemia (FH)',
      description: 'How chronically elevated LDL affects heart valves, aortic root, and heart muscle structure, especially in severe FH cases...',
      category: 'Heart Disease',
      link: '/structural-valvular-heart-disease-fh',
      riskScore: 9
    },
    {
      title: 'Atherosclerosis & Vascular Disease + Familial Hypercholesterolemia (FH)',
      description: 'The buildup of cholesterol-rich plaque inside arteries that begins early in FH patients, leading to premature heart disease and stroke...',
      category: 'Heart Disease',
      link: '/atherosclerosis-vascular-disease-fh',
      riskScore: 9
    },
    {
      title: 'Coronary Artery Disease (CAD) + Familial Hypercholesterolemia (FH)',
      description: 'A condition where the arteries that supply blood to the heart become narrowed, leading to reduced blood flow and increased risk in FH patients...',
      category: 'Heart Disease',
      link: '/coronary-artery-disease-fh',
      riskScore: 9
    },
    {
      title: 'Complications & Outcomes of Familial Hypercholesterolemia (FH)',
      description: 'Understanding the accelerated cardiovascular complications and long-term outcomes in FH patients, from recurrent heart attacks to early mortality risk...',
      category: 'Heart Disease',
      link: '/complications-outcomes-fh',
      riskScore: 10
    },
    {
      title: 'Familial Hypercholesterolemia (FH) in Children (Ages 0–20)',
      description: 'Why early detection in childhood can prevent premature heart disease decades later through screening and treatment...',
      category: 'Heart Disease',
      link: '/fh-children',
      riskScore: 7
    },
    {
      title: 'Diabetes & Insulin Resistance + Familial Hypercholesterolemia (FH)',
      description: 'When FH coexists with diabetes or insulin resistance, cardiovascular risk rises dramatically, accelerating vascular damage and complications...',
      category: 'Diabetes',
      link: '/diabetes-insulin-resistance-fh',
      riskScore: 8
    },
    {
      title: 'Hypertension + Familial Hypercholesterolemia (FH)',
      description: 'When high blood pressure coexists with FH, cardiovascular risk multiplies, accelerating plaque progression and increasing heart attack and stroke risk...',
      category: 'Blood Pressure',
      link: '/hypertension-fh',
      riskScore: 8
    },
    {
      title: 'Kidney Disease + Familial Hypercholesterolemia (FH)',
      description: 'How long-standing high LDL affects kidney blood vessels and function, increasing cardiovascular risk when FH coexists with chronic kidney disease...',
      category: 'Kidney Disease',
      link: '/kidney-disease-fh',
      riskScore: 7
    },
    {
      title: 'Menopause + Familial Hypercholesterolemia (FH)',
      description: 'How declining estrogen levels after menopause accelerate cardiovascular risk in women with FH, requiring reassessment and optimized management...',
      category: 'Women\'s Health',
      link: '/menopause-fh',
      riskScore: 7
    },
    {
      title: 'Stroke + Familial Hypercholesterolemia (FH)',
      description: 'Understanding how lifelong elevated LDL cholesterol increases ischemic stroke risk through carotid artery atherosclerosis and plaque formation...',
      category: 'Emergency',
      link: '/stroke-fh',
      riskScore: 9
    },
    {
      title: 'FH + Food & Exercise Fact vs Myth',
      description: 'Understanding the difference between lifestyle-related cholesterol and genetic FH — why diet and exercise alone cannot normalize genetically elevated LDL...',
      category: 'Lifestyle',
      link: '/fh-food-exercise',
      riskScore: 4
    },
    {
      title: 'Heart-Healthy Diet',
      description: 'A diet rich in fruits, vegetables, whole grains, and lean proteins that supports cardiovascular health...',
      category: 'Lifestyle',
      riskScore: 3
    },
    {
      title: 'High Cholesterol',
      description: 'A condition characterized by high levels of cholesterol in the blood, which can increase the risk...',
      category: 'Cholesterol',
      riskScore: 7
    },
    {
      title: 'Hypertension (High Blood Pressure)',
      description: 'A condition where the force of the blood against the artery walls is consistently too high...',
      category: 'Blood Pressure',
      riskScore: 7
    },
    {
      title: 'Insulin Resistance',
      description: 'A condition where the body\'s cells do not respond properly to insulin, which can lead to...',
      category: 'Diabetes',
      riskScore: 6
    },
    {
      title: 'LDL (Low-Density Lipoprotein)',
      description: 'Known as "bad" cholesterol, LDL can build up in the arteries, increasing the risk of heart disease...',
      category: 'Cholesterol',
      riskScore: 7
    },
    {
      title: 'Lipoprotein(a)',
      description: 'A type of LDL cholesterol that can increase the risk of serious health problems like heart...',
      category: 'Cholesterol',
      riskScore: 8
    },
    {
      title: 'Menopause',
      description: 'A natural biological life transition when menstrual periods permanently stop, often leading to...',
      category: 'Women\'s Health',
      riskScore: 5
    },
    {
      title: 'Metabolic Syndrome',
      description: 'A cluster of conditions including high blood pressure, high blood sugar, excess body fat...',
      category: 'Risk Factors',
      riskScore: 7
    },
    {
      title: 'Obesity',
      description: 'A condition where excess body fat increases the risk of serious health problems including...',
      category: 'Risk Factors',
      riskScore: 6
    },
    {
      title: 'Obstructive Sleep Apnea',
      description: 'A sleep disorder where breathing repeatedly stops and starts during sleep, including insomnia...',
      category: 'Sleep Disorders',
      riskScore: 5
    },
    {
      title: 'Peripheral Artery Disease (PAD)',
      description: 'A condition where narrowed arteries reduce blood flow to the limbs, especially the legs...',
      category: 'Vascular Disease',
      riskScore: 8
    },
    {
      title: 'Physical Activity and Exercise',
      description: 'Regular movement that strengthens the heart, improves circulation, and reduces the risk...',
      category: 'Lifestyle',
      riskScore: 2
    },
    {
      title: 'Postpartum Hypertension',
      description: 'High blood pressure that develops or persists after childbirth, which can lead to serious...',
      category: 'Women\'s Health',
      riskScore: 7
    },
    {
      title: 'Prediabetes',
      description: 'A condition where blood sugar levels are higher than normal but not yet high enough to be...',
      category: 'Diabetes',
      riskScore: 5
    },
    {
      title: 'Preeclampsia',
      description: 'A pregnancy complication characterized by high blood pressure and signs of damage to other...',
      category: 'Women\'s Health',
      riskScore: 9
    },
    {
      title: 'Sleep and Heart Health',
      description: 'The quality of sleep can have a significant impact on cardiovascular health, with poor sleep...',
      category: 'Sleep Disorders',
      riskScore: 4
    },
    {
      title: 'Statins',
      description: 'A group of drugs used to lower blood pressure and reduce the risk of cardiovascular diseases...',
      category: 'Medications',
      riskScore: 3
    },
    {
      title: 'Stress and Heart Health',
      description: 'Chronic stress can negatively affect heart health by raising blood pressure and triggering...',
      category: 'Mental Health',
      riskScore: 5
    },
    {
      title: 'Substance Use and Heart Health',
      description: 'Tobacco, alcohol, and marijuana use can negatively affect heart health by damaging blood vessels...',
      category: 'Risk Factors',
      riskScore: 7
    },
    {
      title: 'Triglycerides',
      description: 'A type of fat in the blood that, when elevated, can increase the risk of heart disease and...',
      category: 'Cholesterol',
      riskScore: 6
    },
    {
      title: 'Type 2 Diabetes',
      description: 'A chronic condition that affects how the body processes blood sugar (glucose), leading to...',
      category: 'Diabetes',
      riskScore: 7
    }
  ];

  const categories = useMemo(() => {
    const cats = new Set<string>();
    topics.forEach(t => cats.add(t.category));
    keyTopics.forEach(t => cats.add(t.category));
    return ['All', ...Array.from(cats).sort()];
  }, []);

  const queryWords = useMemo(() =>
    searchQuery.trim().toLowerCase().split(/\s+/).filter(w => w.length > 0),
    [searchQuery]
  );

  const isSearching = searchQuery.trim().length > 0 || activeCategory !== 'All';

  const filteredKeyTopics = useMemo(() => {
    return keyTopics.filter(topic => {
      const matchesCategory = activeCategory === 'All' || topic.category === activeCategory;
      if (queryWords.length === 0) return matchesCategory;
      const matchesSearch =
        fuzzyMatch(topic.title, queryWords) ||
        fuzzyMatch(topic.description, queryWords) ||
        fuzzyMatch(topic.category, queryWords);
      return matchesCategory && matchesSearch;
    });
  }, [queryWords, activeCategory]);

  const filteredTopics = useMemo(() => {
    return topics.filter(topic => {
      const matchesCategory = activeCategory === 'All' || topic.category === activeCategory;
      if (queryWords.length === 0) return matchesCategory;
      const matchesSearch =
        fuzzyMatch(topic.title, queryWords) ||
        fuzzyMatch(topic.description, queryWords) ||
        fuzzyMatch(topic.category, queryWords);
      return matchesCategory && matchesSearch;
    });
  }, [queryWords, activeCategory]);

  const totalResults = filteredKeyTopics.length + filteredTopics.length;

  const clearSearch = () => {
    setSearchQuery('');
    setActiveCategory('All');
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#fef3f2] via-white to-[#fde8e7]">
      <Navbar />

      {/* FH Key Topics Section */}
      {filteredKeyTopics.length > 0 && (
        <section className="pt-32 pb-12">
          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-10">
              <h2 className="text-3xl font-light text-gray-900 mb-2">
                Understanding - <span className="font-semibold text-[#f04e58]">Familial Hypercholesterolemia (FH)</span>
              </h2>
              <p className="text-sm text-gray-500 font-light">In-depth guides on genetic cholesterol conditions, testing, and treatment</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
              {filteredKeyTopics.map((resource, index) => {
                const risk = getRiskBadge(resource.riskScore);
                const bar = getRiskBar(resource.riskScore);
                return (
                  <Link
                    to={resource.link}
                    key={index}
                    className="bg-white rounded-2xl border border-gray-100 p-6 hover:shadow-xl hover:border-transparent transition-all duration-300 cursor-pointer group relative overflow-hidden"
                  >
                    <div
                      className="absolute top-0 left-0 w-full h-1 rounded-t-2xl"
                      style={{ backgroundColor: resource.color }}
                    ></div>
                    <div className="flex items-start justify-between mb-4">
                      <div
                        className="w-11 h-11 flex items-center justify-center rounded-xl"
                        style={{ backgroundColor: `${resource.color}15` }}
                      >
                        <i className={`${resource.icon} text-xl`} style={{ color: resource.color }}></i>
                      </div>
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border"
                        style={{ color: risk.color, backgroundColor: risk.bg, borderColor: risk.border }}
                      >
                        <i className="ri-shield-line text-[10px]"></i>
                        {risk.label}
                      </span>
                    </div>
                    <h3 className="text-base font-semibold text-gray-900 mb-2 group-hover:text-[#f04e58] transition-colors leading-snug">
                      {highlightMatch(resource.title, searchQuery)}
                    </h3>
                    <p className="text-xs text-gray-500 leading-relaxed font-light mb-3">
                      {highlightMatch(resource.description, searchQuery)}
                    </p>
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-gray-400 font-medium">Risk Score</span>
                        <span className="text-[10px] font-bold" style={{ color: bar.color }}>{resource.riskScore}/10</span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${bar.percentage}%`, backgroundColor: bar.color }}
                        ></div>
                      </div>
                    </div>
                    <span className="inline-flex items-center text-xs font-medium text-[#0d9488] group-hover:text-[#f04e58] transition-colors">
                      Read guide
                      <i className="ri-arrow-right-line ml-1 group-hover:translate-x-1 transition-transform"></i>
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {/* Hero / Knowledge Base Header */}
      <section className="pb-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="text-center max-w-3xl mx-auto">
            <h1 className="text-5xl md:text-6xl font-light text-gray-900 mb-6">
              Knowledge Base
            </h1>
            <p className="text-lg text-gray-700 leading-relaxed font-light">
              Explore a wide range of heart health topics at Vazcular. Get expert advice on prevention, lifestyle changes, and managing your heart health with confidence.
            </p>
          </div>
        </div>
      </section>

      {/* Search Section */}
      <section className="pb-6">
        <div className="max-w-7xl mx-auto px-6">
          <div className="max-w-2xl mx-auto">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none">
                <i className="ri-search-line text-xl text-gray-400"></i>
              </div>
              <input
                type="text"
                placeholder="Search by topic, condition, or keyword..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-12 pr-12 py-4 bg-white border border-gray-200 rounded-xl text-base text-gray-900 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#f04e58] focus:border-transparent transition-all shadow-sm"
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  className="absolute inset-y-0 right-0 pr-4 flex items-center cursor-pointer"
                >
                  <i className="ri-close-circle-fill text-xl text-gray-300 hover:text-gray-500 transition-colors"></i>
                </button>
              )}
            </div>
          </div>
        </div>
      </section>

      {/* Category Filters */}
      <section className="pb-10">
        <div className="max-w-7xl mx-auto px-6">
          <div className="flex flex-wrap justify-center gap-2">
            {categories.map(cat => (
              <button
                key={cat}
                onClick={() => setActiveCategory(cat)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all cursor-pointer whitespace-nowrap ${
                  activeCategory === cat
                    ? 'bg-[#f04e58] text-white shadow-md shadow-[#f04e58]/20'
                    : 'bg-white text-gray-600 border border-gray-200 hover:border-[#f04e58]/40 hover:text-[#f04e58]'
                }`}
              >
                {cat}
              </button>
            ))}
          </div>
          {isSearching && (
            <div className="flex items-center justify-center gap-3 mt-5">
              <p className="text-sm text-gray-500">
                <span className="font-semibold text-gray-700">{totalResults}</span> result{totalResults !== 1 ? 's' : ''} found
                {searchQuery.trim() && <> for &ldquo;<span className="text-[#f04e58] font-medium">{searchQuery.trim()}</span>&rdquo;</>}
                {activeCategory !== 'All' && <> in <span className="text-[#0d9488] font-medium">{activeCategory}</span></>}
              </p>
              <button
                onClick={clearSearch}
                className="text-sm text-gray-400 hover:text-[#f04e58] transition-colors cursor-pointer flex items-center gap-1"
              >
                <i className="ri-close-line text-base"></i>
                Clear
              </button>
            </div>
          )}
        </div>
      </section>

      {/* All Topics Section */}
      {filteredTopics.length > 0 && (
        <section className="pb-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="mb-10">
              <h2 className="text-3xl font-light text-gray-900 mb-2">
                Explore <span className="font-semibold text-[#f04e58]">More</span>
              </h2>
              <p className="text-sm text-gray-500 font-light">Browse our complete A-Z collection of cardiovascular health topics</p>
            </div>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {filteredTopics.map((topic, index) => {
                const risk = getRiskBadge(topic.riskScore);
                const bar = getRiskBar(topic.riskScore);
                return (
                  <Link
                    to={topic.link || '#'}
                    key={index}
                    className="bg-white rounded-2xl border-2 border-[#f04e58]/20 p-6 hover:border-[#f04e58] hover:shadow-lg transition-all duration-300 cursor-pointer group block"
                  >
                    <div className="flex items-center justify-between mb-3">
                      <span className="inline-block px-3 py-1 bg-[#0d9488]/10 text-[#0d9488] text-xs font-medium rounded-full">
                        {topic.category}
                      </span>
                      <span
                        className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-semibold border"
                        style={{ color: risk.color, backgroundColor: risk.bg, borderColor: risk.border }}
                      >
                        <i className="ri-shield-line text-[10px]"></i>
                        {risk.label}
                      </span>
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-3 group-hover:text-[#f04e58] transition-colors">
                      {highlightMatch(topic.title, searchQuery)}
                    </h3>
                    <p className="text-sm text-gray-600 leading-relaxed font-light mb-3">
                      {highlightMatch(topic.description, searchQuery)}
                    </p>
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-[10px] text-gray-400 font-medium">Risk Score</span>
                        <span className="text-[10px] font-bold" style={{ color: bar.color }}>{topic.riskScore}/10</span>
                      </div>
                      <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                        <div
                          className="h-full rounded-full transition-all duration-500"
                          style={{ width: `${bar.percentage}%`, backgroundColor: bar.color }}
                        ></div>
                      </div>
                    </div>
                    <span className="inline-flex items-center text-sm font-medium text-[#0d9488] hover:text-[#f04e58] transition-colors whitespace-nowrap">
                      Read more
                      <i className="ri-arrow-right-line ml-1"></i>
                    </span>
                  </Link>
                );
              })}
            </div>
          </div>
        </section>
      )}

      {totalResults === 0 && (
        <section className="pb-20">
          <div className="max-w-7xl mx-auto px-6">
            <div className="text-center py-16">
              <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gray-100 mx-auto mb-4">
                <i className="ri-search-line text-4xl text-gray-400"></i>
              </div>
              <h3 className="text-xl font-semibold text-gray-900 mb-2">No topics found</h3>
              <p className="text-gray-600 mb-6">Try different keywords or browse by category</p>
              <button
                onClick={clearSearch}
                className="px-6 py-2.5 bg-[#f04e58] text-white text-sm font-medium rounded-full hover:bg-[#d9434c] transition-colors cursor-pointer whitespace-nowrap"
              >
                Clear all filters
              </button>
            </div>
          </div>
        </section>
      )}

      <Footer />
    </div>
  );
};

export default Topics;
